import { useEnvStore } from '@/stores/envStore';
import { ref } from 'vue';
import { useFetch } from './useFetch';
import { useFileDownload } from './useFileDownload';

export const useManager = () => {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const envStore = useEnvStore();
  const { fetchData } = useFetch();

  async function getTeam(restaurant_id: string) {
    console.log('getTeam');
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/manager/${restaurant_id}/team`, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }
      console.log('getTeam data', data);

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'An error occurred';
      }

      return {
        data: null,
        error: error.value,
      };
    }
  }

  async function getUserDetails(restaurant_id: string, userId: string) {
    console.log('getUserDetails');
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/manager/${restaurant_id}/team/${userId}`, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }
      console.log('getUserDetails data', data);

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'An error occurred';
      }

      return {
        data: null,
        error: error.value,
      };
    }
  }

  async function exportWorkerHours(restaurant_user_id: number | string, startDate?: string, endDate?: string) {
    isLoading.value = true;
    error.value = null;

    try {
      // Build query parameters
      const params = new URLSearchParams({
        restaurant_user_id: restaurant_user_id.toString(),
      });

      if (startDate) {
        params.append('start_date', startDate);
      }

      if (endDate) {
        params.append('end_date', endDate);
      }

      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/manager/export-working-periods?${params.toString()}`, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }
      console.log('exportWorkerHours data', data);

      // Download the CSV data
      const dateRange = startDate && endDate ? `${startDate}_to_${endDate}` : 'current_month';
      await downloadCSV(data, `worker-hours-${restaurant_user_id}-${dateRange}.csv`);

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'An error occurred';
      }

      return {
        data: null,
        error: error.value,
      };
    }
  }

  async function downloadCSV(csvData: string, filename: string) {
    // Create a Blob with the CSV data
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const { downloadFile } = useFileDownload();

    try {
      await downloadFile({
        fileName: filename,
        data: blob,
        mimeType: 'text/csv',
        showShareDialog: true,
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      // Fallback to browser download for web
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }
  }

  async function generateNewInvitationLink(restaurant_id: string, user_id: string) {
    isLoading.value = true;
    error.value = null;

    try {
      console.log('TESTESA FJkdsj');
      const { data, error: fetchError } = await fetchData(
        `${envStore.apiUrl}/manager/${restaurant_id}/team/${user_id}/generate-invitation-link`,
        'GET',
      );
      console.log('TESTESA FJkdsj', data);

      isLoading.value = false;

      if (fetchError) {
        return { data: null, error: fetchError };
      }

      return { data, error: null };
    } catch (err: unknown) {
      isLoading.value = false;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'An error occurred';
      }
      return { data: null, error: error.value };
    }
  }

  const resendInvitationLink = async (restaurantId: string, userId: string) => {
    // console.log('Resending invitation link for user:', userId);
    const { apiUrl } = useEnvStore();
    try {
      // console.log('Resending invitation link for user:', userId);
      const response = await fetchData(`${apiUrl}/manager/${restaurantId}/team/${userId}/resend-invitation`, 'POST', {});
      // console.log('Response from resend invitation link:', response);
      return { data: response.data, error: response.error };
    } catch (error) {
      console.error('Error resending invitation link:', error);
      return { data: null, error: error };
    }
  };

  /**
   * Get a team member by restaurant_user_id (more efficient than fetching entire team)
   */
  async function getTeamMemberByRestaurantUserId(restaurantId: string, restaurantUserId: number | string) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(
        `${envStore.apiUrl}/manager/${restaurantId}/team/by-restaurant-user/${restaurantUserId}`,
        'GET',
      );

      isLoading.value = false;

      if (fetchError) {
        return { data: null, error: fetchError };
      }

      return { data, error: null };
    } catch (err: unknown) {
      isLoading.value = false;
      if (err instanceof Error) {
        error.value = err.message;
      } else {
        error.value = 'An error occurred';
      }

      return { data: null, error: error.value };
    }
  }

  return {
    getTeam,
    getUserDetails,
    getTeamMemberByRestaurantUserId,
    exportWorkerHours,
    generateNewInvitationLink,
    resendInvitationLink,
    isLoading,
    error,
  };
};
